import { assertUnreachable } from "../helpers";
import { Part, QuoteRequestLine } from "../types";
import {
  AssemblyWithChildren,
  groupQuoteRequestLinesByBatch,
} from "./groupQuoteRequestLinesByBatch";

export type SortProperty = "name" | "createdAt" | "index";
export type SortOrder = "asc" | "desc";

export type SortConfig = {
  property: SortProperty;
  order: SortOrder;
};

export type SortableQuoteRequestLine = QuoteRequestLine & {
  part: Pick<Part, "id" | "name">;
};

export type SortedQuoteRequestLines<T extends SortableQuoteRequestLine> = {
  batches: Map<string, AssemblyWithChildren<T>[]>;
  assemblies: AssemblyWithChildren<T>[];
};

export const sortAssembliesByCreatedAt = <T extends SortableQuoteRequestLine>(
  assemblies: AssemblyWithChildren<T>[],
): AssemblyWithChildren<T>[] =>
  assemblies.slice().sort((a, b) => compareCreatedAt(a.quoteRequestLine, b.quoteRequestLine));

export const sortQuoteRequestLines = <T extends SortableQuoteRequestLine>(
  quoteRequestLines: T[],
  sortConfig?: SortConfig,
): SortedQuoteRequestLines<T> => {
  const sortedBatches = new Map(
    Array.from(groupQuoteRequestLinesByBatch(quoteRequestLines).entries())
      .map(([key, assembliesInBatch]) => {
        const sortedByCreatedAt = sortAssembliesByCreatedAt(assembliesInBatch);
        const sortedAssemblies = sortConfig
          ? sortedByCreatedAt.map((assembly) => sortAssemblyChildren(assembly, sortConfig))
          : sortedByCreatedAt;
        return [key, sortedAssemblies] as const;
      })
      .sort(([, leftBatch], [, rightBatch]) => {
        if (!sortConfig) {
          return 0;
        }
        const left = leftBatch[0]?.quoteRequestLine;
        const right = rightBatch[0]?.quoteRequestLine;
        if (!left || !right) {
          return 0;
        }
        return compareQuoteRequestLines(left, right, sortConfig);
      }),
  );

  return {
    batches: sortedBatches,
    assemblies: Array.from(sortedBatches.values()).flat(),
  };
};

const sortAssemblyChildren = <T extends SortableQuoteRequestLine>(
  assembly: AssemblyWithChildren<T>,
  sortConfig: SortConfig,
): AssemblyWithChildren<T> => ({
  ...assembly,
  children: assembly.children
    .map((child) => sortAssemblyChildren(child, sortConfig))
    .sort((a, b) => compareQuoteRequestLines(a.quoteRequestLine, b.quoteRequestLine, sortConfig)),
});

const compareCreatedAt = (a: SortableQuoteRequestLine, b: SortableQuoteRequestLine) =>
  toTimestamp(a.createdAt) - toTimestamp(b.createdAt);

const compareQuoteRequestLines = (
  a: SortableQuoteRequestLine,
  b: SortableQuoteRequestLine,
  sortConfig: SortConfig,
) => {
  const sortingFactor = sortConfig.order === "asc" ? 1 : -1;

  switch (sortConfig.property) {
    case "createdAt":
      return sortingFactor * compareCreatedAt(a, b);
    case "index":
      return sortingFactor * (a.index - b.index);
    case "name":
      return sortingFactor * a.part.name.localeCompare(b.part.name);
    default:
      return assertUnreachable(sortConfig.property);
  }
};

const toTimestamp = (date: string | Date) => {
  if (typeof date === "string") {
    const timestamp = Date.parse(date);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
  const timestamp = date.getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};
